import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  inject,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
  DOCUMENT,
} from '@angular/core';
import {
  faUserCircle,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { IOrganizationSubscriptionType } from '../../shared/models/dtos/get/organization-subscription-type.interface';
import { ISubscriptionType } from '../../shared/models/dtos/get/subscription-type.interface';
import { environment } from '../../../environments/environment';
import {
  NgbDropdownModule,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { SharedUserService } from '../../shared/services/shared/user/shared-user.service';
import { SubscriptionTypeService } from '../../shared/services/transactional/subscription-type/subscription-type.service';
import { SharedOrganizationService } from '../../shared/services/shared/organization/shared-organization.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { OrganizationSubscriptionTypeService } from '../../shared/services/transactional/organization-subscription-type/organization-subscription-type.service';
import { SpinnerService } from '../../shared/services/shared/spinner/spinner.service';
import { RazorpayService } from '../../shared/services/transactional/razorpay/razorpay.service';
import { WindowRefService } from '../../shared/services/window-ref/window-ref.service';
import { finalize, mergeMap, Observable } from 'rxjs';
import { IResponse } from '../../shared/models/dtos/response.interface';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NavbarControlComponent } from '../../shared/controls/navbar-control/navbar-control.component';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { loadComponentTranslations } from '../../shared/funcs/lang-loader';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-pricing',
  imports: [
    CommonModule,
    TranslateModule,
    FontAwesomeModule,
    RouterModule,
    NgbDropdownModule,
    NavbarControlComponent,
  ],
  templateUrl: './pricing.component.html',
  styleUrl: './pricing.component.scss',
})
export class PricingComponent {
  public isLoaded: boolean = false;
  public organizationId: string = null!;
  public userId: string = null!;
  public faUserCircle: IconDefinition = faUserCircle;
  public subscriptionTypes: ISubscriptionType[] = [];
  public environment = environment;

  @ViewChild('selectedPlanModal') public selectedPlanModal: TemplateRef<any> =
    null!;

  public modalRef: NgbModalRef = null!;
  public modal: NgbModal = inject(NgbModal);

  constructor(
    private _subscriptionTypeService: SubscriptionTypeService,
    private metaService: Meta,
    private _titleService: Title,
    private _translateService: TranslateService,
    private _http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private _router: Router,
  ) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  public ngOnInit(): void {
    loadComponentTranslations(
      this._http,
      this._translateService,
      'components/pricing',
      this.isBrowser(),
    ).subscribe({
      next: () => {},
      error: () => {},
    });

    // ✅ Page Title (commercial intent)
    this._titleService.setTitle(
      'ClockFlex Pricing | Simple & Flexible Scheduling Plans',
    );

    // ✅ Canonical URL
    const canonicalUrl = `https://clockflex.com${this._router.url.split('?')[0]}`;
    this.setCanonicalUrl(canonicalUrl);

    // ✅ Meta Description
    this.metaService.updateTag({
      name: 'description',
      content:
        'Compare ClockFlex pricing plans and choose the right scheduling solution for your business. Flexible plans built for professionals and growing teams.',
    });

    // ❌ DO NOT add meta robots (server controls it)

    // ✅ Open Graph
    this.metaService.updateTag({
      property: 'og:title',
      content: 'ClockFlex Pricing | Simple & Flexible Scheduling Plans',
    });

    this.metaService.updateTag({
      property: 'og:description',
      content:
        'Explore ClockFlex pricing plans designed for professionals and teams. Simple, transparent, and flexible scheduling solutions.',
    });

    this.metaService.updateTag({
      property: 'og:url',
      content: canonicalUrl,
    });

    this.metaService.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.metaService.updateTag({
      property: 'og:site_name',
      content: 'ClockFlex',
    });

    // ✅ Twitter
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.metaService.updateTag({
      name: 'twitter:title',
      content: 'ClockFlex Pricing | Simple & Flexible Scheduling Plans',
    });

    this.metaService.updateTag({
      name: 'twitter:description',
      content:
        'Choose the right ClockFlex plan to manage bookings, availability, and scheduling with ease.',
    });

    this.metaService.updateTag({
      name: 'twitter:image',
      content: 'https://clockflex.com/assets/images/og-image.png',
    });

    this.loadSubscriptionTypes();
  }

  private setCanonicalUrl(url: string): void {
    if (!url.startsWith('http')) {
      return;
    }

    let link: HTMLLinkElement | null = this.document.querySelector(
      "link[rel='canonical']",
    );

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  public loadSubscriptionTypes() {
    this._subscriptionTypeService
      .get(this.userId)
      .pipe(
        finalize(() => {
          this.isLoaded = true;
        }),
      )
      .subscribe((response) => {
        console.log(response);

        if (response.isSuccess) {
          if (response.data) {
            this.subscriptionTypes = response.data;
          }
        }
      });
  }

  public getAmount(sub: ISubscriptionType): number {
    let discount = sub.amount * ((sub.discountInPercentage || 0) / 100);
    return sub.amount - discount;
  }

  public getBookableCalendars(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find((f) => f.name === 'AllowCalendars')
      ?.value!;
  }

  public getBookableAppointmentTypes(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find(
      (f) => f.name === 'AllowAppointmentTypes',
    )?.value!;
  }

  public getBookableContributors(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find((f) => f.name === 'AllowContributors')
      ?.value!;
  }

  public getIntakeForms(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find(
      (f) => f.name === 'AllowIntakeFormsNumbers',
    )?.value!;
  }

  public allowIntakeForms(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find((f) => f.name === 'AllowIntakeForms')
      ?.value!;
  }

  public allowEmailNotification(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find(
      (f) => f.name === 'AllowEmailNotifications',
    )?.value!;
  }
  public allowEmailReminders(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find(
      (f) => f.name === 'AllowEmailReminders',
    )?.value!;
  }

  public allowSMSNotification(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find(
      (f) => f.name === 'AllowSMSNotifications',
    )?.value!;
  }

  public allowSMSReminders(sub: ISubscriptionType): number {
    return +sub.subscriptionFeatures.find((f) => f.name === 'AllowSMSReminders')
      ?.value!;
  }
}
